import { useMemo } from 'react';
import { parseDate, isValidDate, calculateSLAStatus } from '../../../utils/slaDashboardUtils';
import { AT_RISK_HOURS_THRESHOLD } from '../../../constants/slaDashboard';

/**
 * Hook to calculate SLA trend data for line chart (last 6 months)
 * @param {Array} queries - Array of queries
 * @param {Date} now - Current date
 * @returns {Array} SLA trend data
 */
export const useSLATrendData = (queries, now) => {
  return useMemo(() => {
    const trendMap = new Map();

    queries.forEach((query) => {
      if (!query.raisedAt) return;
      const due = parseDate(query.slaDueDate);
      // Only process queries with SLA due dates
      if (!due || !isValidDate(due)) {
        return;
      }

      const date = parseDate(query.raisedAt);
      if (!date || !isValidDate(date)) return;

      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (!trendMap.has(monthKey)) {
        trendMap.set(monthKey, { total: 0, within: 0, breached: 0 });
      }

      const entry = trendMap.get(monthKey);
      entry.total += 1;

      const respondedAt = parseDate(query.respondedAt);
      const closedAt = parseDate(query.closedAt);
      const completedAt = respondedAt || closedAt;

      const status = calculateSLAStatus(due, completedAt, now, AT_RISK_HOURS_THRESHOLD);

      if (status === 'Breached') {
        entry.breached += 1;
      } else {
        entry.within += 1;
      }
      trendMap.set(monthKey, entry);
    });

    const sortedKeys = Array.from(trendMap.keys()).sort();
    const lastSixKeys = sortedKeys.slice(-6);

    return lastSixKeys.map((key) => {
      const data = trendMap.get(key);
      const [year, month] = key.split('-');
      const monthLabel = new Date(Number(year), Number(month) - 1).toLocaleString('default', { month: 'short' });
      return {
        month: `${monthLabel} ${year}`,
        adherence: data.total > 0 ? Number(((data.within / data.total) * 100).toFixed(1)) : 0,
        breached: data.breached,
      };
    });
  }, [queries, now]);
};
